/*
 *
 * SavingWallet saga
 *
 */

import { call, put, takeLatest, select } from 'redux-saga/effects';
import {
  getWalletSummary,
  getWalletSummarySuccess,
  getWalletSummaryError,
  getWalletTransactions,
  getWalletTransactionsSuccess,
  getWalletTransactionsError,
  showMilestoneCelebration,
} from './actions';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS, APIHeadersWithAuth, getCountryApiEndpoints } from '../../globalConstants';
import { walletSummaryResponseSchema, walletTransactionsResponseSchema } from '../../apischema/savingwallet';
import { savingWalletSelectors } from './selectors';
import { checkMilestoneAchievement } from './milestoneUtils';
import { getAuthToken } from '../../utils/helper';
import { selectCountryCode } from '../../store/countrySlice';

function* getWalletSummarySaga(): Generator<any, void, any> {
  try {
    // Get previous points from state to check for milestone achievements
    const previousPoints = yield select(savingWalletSelectors.previousPoints);

    // Get authentication token
    const token = yield call(getAuthToken);

    if (!token) {
      throw new Error('Authentication token not available');
    }

    const countryCode: string = yield select(selectCountryCode);
    const endpoints = getCountryApiEndpoints(countryCode, 'savingWalletPage');

    const response = yield call(apiClient.request, endpoints.savingWallet.GET_SUMMARY, {
      method: 'GET',
      headers: APIHeadersWithAuth(token),
    });

    // Validate API response with schema
    const parsed = walletSummaryResponseSchema.safeParse(response);
    if (!parsed.success) {
      throw new Error('Invalid API response: ' + JSON.stringify(parsed.error.issues));
    }

    yield put(getWalletSummarySuccess(parsed.data));

    // Check for milestone achievement
    const currentPoints = parsed.data.data.totalPoints;

    // Find if user has crossed any milestone
    const achievedMilestone = checkMilestoneAchievement(previousPoints, currentPoints);

    if (achievedMilestone) {
      yield put(showMilestoneCelebration(achievedMilestone));
    }

  } catch (error) {
    yield put(getWalletSummaryError(error instanceof Error ? error.message : 'An error occurred'));
  }
}

function* getWalletTransactionsSaga(): Generator<any, void, any> {
  try {
    // Get authentication token
    const token = yield call(getAuthToken);

    if (!token) {
      throw new Error('Authentication token not available');
    }

    const countryCode: string = yield select(selectCountryCode);
    const endpoints = getCountryApiEndpoints(countryCode, 'savingWalletPage');

    const response = yield call(apiClient.request, endpoints.savingWallet.GET_TRANSACTIONS, {
      method: 'GET',
      headers: APIHeadersWithAuth(token),
    });

    // Validate API response with schema
    const parsed = walletTransactionsResponseSchema.safeParse(response);
    if (!parsed.success) {
      console.error('Schema validation error:', parsed.error.issues);
      throw new Error('Invalid API response: ' + JSON.stringify(parsed.error.issues));
    }

    // The response structure is: { success, message, status, data: { hasSavingWallet, totalPoints, history: [...] } }
    yield put(getWalletTransactionsSuccess(parsed.data));

  } catch (error) {
    console.error('Wallet transactions saga error:', error);
    yield put(getWalletTransactionsError(error instanceof Error ? error.message : 'An error occurred'));
  }
}

export default function* savingWalletSaga() {
  yield takeLatest(getWalletSummary.type, getWalletSummarySaga);
  yield takeLatest(getWalletTransactions.type, getWalletTransactionsSaga);
}
